import {createContext, ReactNode, useContext, useEffect, useState} from "react";

type BeforeInstallPromptEvent = Event & {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>
};

const Context = createContext<ClientContextData>(null!);

export default function ClientContext(props: {children: ReactNode}) {
    const [standalone, setStandalone] = useState(false)
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const [canInstall, setCanInstall] = useState(false)

    useEffect(() => {
        const checkStandalone = () => {
            const isStandalone = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;
            setStandalone(isStandalone);
        };

        checkStandalone();

        window.matchMedia('(display-mode: standalone)').addEventListener('change', checkStandalone);

        return () => window.matchMedia('(display-mode: standalone)').removeEventListener('change', checkStandalone);
    }, []);

    useEffect(() => {
        const handler = (e: Event) => {
            const event = e as BeforeInstallPromptEvent;
            event.preventDefault();
            setDeferredPrompt(event)
            if (!standalone){
                setCanInstall(true)
            }
        };

        window.addEventListener("beforeinstallprompt", handler);

        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, [standalone])

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        await deferredPrompt.prompt();

        const result = await deferredPrompt.userChoice;
        console.log("PWA install result:", result);

        setDeferredPrompt(null);
        setCanInstall(false);
    };

    const context: ClientContextData = {
        isStandalone: standalone,
        canInstall: canInstall,
        doInstall: handleInstall
    }

    return (
        <Context.Provider value={context}>
            {props.children}
        </Context.Provider>
    )
}

export const useClient = () => useContext(Context)

interface ClientContextData{
    isStandalone: boolean
    canInstall: boolean
    doInstall: () => Promise<void>
}